/**
 * Iter-225a — Shared certification badge component.
 *
 * Renders the assessment-based `training_certifications` array
 * surfaced on every talent surface (Discovery card, talent profile,
 * admin UserDetail, client picker, public booking profile, talent
 * dashboard).
 *
 * Props
 *   certifications: [{ id, module_name, module_slug, badge_name,
 *                      badge_icon, score, pass_score, passed_at,
 *                      expires_at, is_expired }, ...]
 *   variant: 'compact'   — single-line pill row (Discovery cards)
 *            'detailed'  — multi-line with score + date (profile pages)
 *            'inline'    — comma-separated text (table cells)
 *   showScore: bool      — show "92%" next to the badge name (default true)
 *   showDate:  bool      — show "Earned MM-DD-YYYY" (default true on detailed)
 *   maxVisible: number   — collapse the rest into "+N more"
 */
import React from 'react';
import {
  Award, ShieldCheck, Star, GraduationCap, Sparkles, BadgeCheck,
  Utensils, Wine, Megaphone, Users, Shield, Calendar as CalendarIcon,
  MapPin, Package,
} from 'lucide-react';
import { Badge } from './ui/badge';

const ICON_MAP = {
  'shield-check': ShieldCheck,
  'star': Star,
  'graduation-cap': GraduationCap,
  'sparkles': Sparkles,
  'badge-check': BadgeCheck,
  'award': Award,
  // Iter-225b/c/g — icons referenced by V175 + V176 modules.
  'utensils': Utensils,
  'wine': Wine,
  'megaphone': Megaphone,
  'users': Users,
  'shield': Shield,
  'calendar': CalendarIcon,
  'map-pin': MapPin,
  'package': Package,
};

function _formatMDY(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}-${dd}-${d.getFullYear()}`;
  } catch { return null; }
}

const CertificationBadges = ({
  certifications = [],
  variant = 'compact',
  showScore = true,
  showDate = null,            // null → defaults per variant
  maxVisible = null,          // null → no cap
  testIdPrefix = 'cert-badge',
}) => {
  if (!Array.isArray(certifications) || certifications.length === 0) {
    return null;
  }
  const dateDefault = variant === 'detailed';
  const renderDate = showDate === null ? dateDefault : showDate;

  // Sort active before expired, then by passed_at desc
  const sorted = [...certifications].sort((a, b) => {
    if (a.is_expired !== b.is_expired) return a.is_expired ? 1 : -1;
    return (b.passed_at || '').localeCompare(a.passed_at || '');
  });
  const visible = maxVisible ? sorted.slice(0, maxVisible) : sorted;
  const overflow = sorted.length - visible.length;

  if (variant === 'inline') {
    return (
      <span className="text-xs text-slate-600" data-testid={`${testIdPrefix}-inline`}>
        {visible.map((c, i) => (
          <span key={c.id} className={c.is_expired ? 'opacity-60' : ''}>
            {c.badge_name}
            {showScore && c.score != null && ` (${c.score}%)`}
            {i < visible.length - 1 && ', '}
          </span>
        ))}
        {overflow > 0 && <span className="text-slate-500"> · +{overflow} more</span>}
      </span>
    );
  }

  return (
    <div
      className={`flex flex-wrap gap-1.5 ${variant === 'detailed' ? 'flex-col gap-2 items-start' : 'items-center'}`}
      data-testid={`${testIdPrefix}-list`}
    >
      {visible.map((c) => {
        const Icon = ICON_MAP[c.badge_icon] || ICON_MAP.award;
        const baseCls = c.is_expired
          ? 'bg-slate-100 text-slate-500 border-slate-200'
          : 'bg-emerald-50 text-emerald-700 border-emerald-300';
        if (variant === 'detailed') {
          return (
            <div
              key={c.id}
              className={`flex items-start gap-2 px-2.5 py-1.5 rounded-md border ${baseCls}`}
              data-testid={`${testIdPrefix}-${c.module_slug}`}
              title={c.is_expired ? 'Expired' : 'Active certification'}
            >
              <Icon className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-semibold leading-tight">
                  {c.badge_name}
                  {showScore && c.score != null && (
                    <span className="ml-1 text-emerald-700/80">— {c.score}%</span>
                  )}
                  {c.is_expired && (
                    <span className="ml-1 text-rose-600 text-[10px] font-bold">(EXPIRED)</span>
                  )}
                </span>
                {renderDate && _formatMDY(c.passed_at) && (
                  <span className="text-[10px] text-slate-500">
                    Earned {_formatMDY(c.passed_at)}
                    {c.expires_at && _formatMDY(c.expires_at) && (
                      <> · Expires {_formatMDY(c.expires_at)}</>
                    )}
                  </span>
                )}
              </div>
            </div>
          );
        }
        // compact
        return (
          <Badge
            key={c.id}
            className={`text-[10px] px-1.5 py-0.5 font-semibold border ${baseCls}`}
            data-testid={`${testIdPrefix}-${c.module_slug}`}
            title={
              `${c.badge_name}` +
              (c.score != null ? ` — ${c.score}%` : '') +
              (c.passed_at ? `\nEarned ${_formatMDY(c.passed_at)}` : '') +
              (c.expires_at ? `\nExpires ${_formatMDY(c.expires_at)}` : '') +
              (c.is_expired ? '\n(Expired)' : '')
            }
          >
            <Icon className="w-3 h-3 mr-0.5" />
            {c.badge_name}
            {showScore && c.score != null && (
              <span className="ml-0.5">· {c.score}%</span>
            )}
          </Badge>
        );
      })}
      {overflow > 0 && (
        <Badge className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 border-slate-200">
          +{overflow} more
        </Badge>
      )}
    </div>
  );
};

export default CertificationBadges;
